import { useEffect, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import type { StudioForm } from '../studio/studio-api';
import { useCircleLocalMedia } from '../studio/circle/use-circle-local-media';
import type { UserAvatar } from '../../api/people';
import { StudioPromptField } from '../studio/StudioPromptField';
import { videoPolicy } from '../video/video-model';
import { videoPromptPresets } from '../video/video-prompt-presets';
import { record } from '../studio/studio-content';
import { PhotoCrop } from './PhotoCrop';
import { photoLabels, photoValues, photoPolicy } from './photo-model';
import { VideoSource } from '../video/VideoSource';
import { ChoiceTiles } from '../../ui/ChoiceTiles';
import { photoPromptPresets } from './photo-prompt-presets';
import type { StudioUploadExampleData } from '../studio/StudioUploadExample';
import { PROMPT_MAX } from '../studio/prompt-limit';
export type PhotoField = StudioForm['fields'][number];
async function imageDimensions(file: File): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const image = new Image();
      const timer = setTimeout(() => reject(new Error('image_timeout')), 10000);
      image.onload = () => {
        clearTimeout(timer);
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };
      image.onerror = () => {
        clearTimeout(timer);
        reject(new Error('image_invalid'));
      };
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
export function PhotoFieldInput({
  field,
  state,
  avatar,
  label,
  valueLabels,
  presets,
  chipMode,
  hint,
  note,
  placeholder,
  descriptions,
  prices,
  frame,
}: {
  field: PhotoField;
  state: CircleFormState;
  avatar: UserAvatar | null;
  label?: string;
  valueLabels?: Record<string, string>;
  /** Чипы текстового поля по месту (§2.2, §3): «Улыбка · Деловой образ · Летний фон» и т. п. */
  presets?: ReadonlyArray<readonly [string, string]>;
  chipMode?: 'set' | 'add';
  /** «необязательно» у текстовой секции листа */
  hint?: string;
  note?: string;
  /** Подсказка пустого поля описания по виду (K.place шаблона); нет — общая подсказка поля */
  placeholder?: string;
  /** Пояснения строк выбора по значению («Для Reels, Shorts и сторис») */
  descriptions?: Record<string, string>;
  /** Подпись цены строки по значению («30 C»), если форма публикует цену значения */
  prices?: Record<string, number>;
  /** Рамка загрузки главного фото: подпись экрана и пример «Было · Стало» пресета */
  frame?: { subtitle?: string; example?: StudioUploadExampleData | null };
}) {
  const [error, setError] = useState(''),
    [reading, setReading] = useState(false);
  const generation = useRef(0);
  const priorStyle = useRef<string | undefined>(undefined);
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  const local = useCircleLocalMedia();
  const setValue = (value: string) =>
    state.setInputs(current => {
      const next = { ...current, [field.key]: value };
      if (field.key === 'format' && value === 'auto') {
        delete next.crop_x;
        delete next.crop_y;
        delete next.zoom;
      }
      return next;
    });
  const limit =
    field.key === 'description' && state.form
      ? Math.min(field.max_length ?? PROMPT_MAX, photoPolicy(state.form)?.description_max ?? PROMPT_MAX)
      : (field.max_length ?? PROMPT_MAX);
  if (field.upload?.kind === 'photo' && field.key === 'photo')
    return (
      <>
        <VideoSource
          field={field}
          state={state}
          uploadLabel={photoLabels[field.key] ?? 'Фото'}
          title={label ?? 'Фото'}
          subtitle={frame?.subtitle}
          example={frame?.example}
        />
        {avatar?.image_url && !state.uploadIds[field.key] && (
          <button type="button" disabled={state.locked || !state.rights} onClick={() => void state.importAvatarPhoto(field.key)}>
            {uiText('Использовать фото выбранного варианта')}
          </button>
        )}
      </>
    );
  if (field.upload) {
    const maximum = field.upload.max_bytes ?? 10 * 1024 * 1024;
    const select = async (file: File | undefined) => {
      if (!file) return;
      const current = ++generation.current;
      setReading(true);
      setError('');
      try {
        if (!field.upload!.accept.includes(file.type) || file.size > maximum)
          throw new Error(uiFormat('Выберите подходящее фото до {0} МБ.', maximum / 1024 / 1024));
        if (field.upload!.purpose === 'reference') {
          const size = await imageDimensions(file);
          if (size.width < 256 || size.height < 256) throw new Error(uiText('Референс должен быть не меньше 256 × 256 пикселей.'));
        }
        if (generation.current !== current) return;
        if (field.key === 'reference') {
          priorStyle.current = state.inputs.style ?? priorStyle.current;
          state.setInputs(current => {
            const next = { ...current };
            next.style = 'none';
            return next;
          });
        }
        local.select(file);
        await state.upload(field.key, file);
      } catch (failure) {
        if (generation.current === current) setError(failure instanceof Error ? failure.message : uiText('Не удалось прочитать фото.'));
      } finally {
        if (generation.current === current) setReading(false);
      }
    };
    return (
      <div className={`photo-field${field.key === 'reference' ? ' studio-reference-upload' : ''}`}>
        <label className={field.key === 'reference' ? 'bcu-pl' : undefined}>
          {uiText(field.key === 'reference' ? 'Загрузить картинку' : (photoLabels[field.key] ?? 'Фото'))}
          <input
            aria-label={uiText(photoLabels[field.key] ?? 'Фото')}
            type="file"
            className={field.key === 'reference' ? 'studio-native-file-input' : undefined}
            accept={field.upload.accept.join(',')}
            disabled={state.locked || !state.rights || reading || !!state.uploadIds[field.key]}
            onChange={event => {
              void select(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </label>
        {field.key === 'photo' && (
          <label>
            {uiText('Снять фото')}
            <input
              aria-label={uiText('Снять фото')}
              type="file"
              capture="user"
              accept={field.upload.accept.join(',')}
              disabled={state.locked || !state.rights || reading || !!state.uploadIds[field.key]}
              onChange={event => {
                void select(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </label>
        )}
        <small>
          {field.upload.accept.map(mime => mime.split('/')[1].toUpperCase()).join(', ')} · {uiFormat('До {0} МБ', maximum / 1024 / 1024)}
        </small>
        {local.preview && <img className="photo-source-preview" src={local.preview.url} alt={uiText('Выбранное фото')} />}
        {local.preview && field.key === 'photo' && <PhotoCrop url={local.preview.url} state={state} />}
        {reading && <p role="status">{uiText('Проверяем фото…')}</p>}
        {error && <p role="alert">{error}</p>}
        {state.inputs[field.key] && <p role="status">{uiText('Загружено')}</p>}
        {field.key === 'reference' && state.uploadIds.reference && !state.inputs.reference && !!state.inputs.style && (
          <button
            type="button"
            disabled={state.locked || !state.rights}
            onClick={() => {
              priorStyle.current = state.inputs.style ?? priorStyle.current;
              state.setInputs(current => {
                const next = { ...current };
                next.style = 'none';
                return next;
              });
              void state.upload(field.key, null);
            }}
          >
            {uiText('Использовать референс')}
          </button>
        )}
        {(state.uploadIds[field.key] || state.inputs[field.key]) && (
          <button
            type="button"
            disabled={state.locked}
            onClick={() => {
              const activeReference = field.key === 'reference' && !!state.inputs.reference;
              state.clearUpload(field.key);
              local.select(null);
              setError('');
              if (activeReference) {
                const fallback = priorStyle.current ?? photoPolicy(state.form!)?.default_style_id;
                if (fallback) state.setInputs(current => ({ ...current, style: String(fallback) }));
              }
            }}
          >
            {uiText('Убрать')}
          </button>
        )}
        {state.uploadIds[field.key] && (
          <button
            type="button"
            disabled={state.locked}
            onClick={() => {
              state.clearUpload(field.key);
              local.select(null);
              setError('');
            }}
          >
            {uiText('Заменить файл')}
          </button>
        )}
        {state.uploadIds[field.key] && !state.inputs[field.key] && !(field.key === 'reference' && state.inputs.style) && (
          <button type="button" disabled={state.busy} onClick={() => void state.upload(field.key, null)}>
            {uiText('Продолжить проверку')}
          </button>
        )}
        {field.key === 'photo' && avatar?.image_url && !state.uploadIds[field.key] && (
          <button type="button" disabled={state.locked || !state.rights} onClick={() => void state.importAvatarPhoto(field.key)}>
            {uiText('Использовать фото выбранного варианта')}
          </button>
        )}
      </div>
    );
  }
  if (field.key === 'description') {
    const video = state.form ? videoPolicy(state.form) : null;
    const photo = state.form ? photoPolicy(state.form) : null;
    return (
      <StudioPromptField
        label={uiText(label ?? 'Описание')}
        value={state.inputs[field.key] ?? ''}
        onChange={setValue}
        minimum={video ? Number(video.description_min_length ?? 0) : (photo?.description_min ?? 0)}
        limit={video ? Math.min(limit, Number(video.description_max_length ?? PROMPT_MAX)) : limit}
        disabled={state.locked}
        kind={video ? 'video' : 'photo'}
        presets={presets ?? (video?.mode === 'text' ? videoPromptPresets : photo?.mode === 'generate' ? photoPromptPresets : [])}
        chipMode={chipMode}
        hint={hint}
        note={note}
        placeholder={placeholder}
        improvement={
          video ? record(video.prompt_improvement).available === true : state.form?.constraints?.photo?.improvement_available === true
        }
      />
    );
  }
  if (field.values.length)
    return (
      <ChoiceTiles
        label={uiText(label ?? photoLabels[field.key] ?? 'Дополнительная настройка')}
        value={state.inputs[field.key] ?? ''}
        disabled={state.locked}
        variant={['format', 'count'].includes(field.key) ? 'rows' : 'segments'}
        choices={[
          ...(!field.required && !['format', 'count'].includes(field.key) ? [{ value: '', label: uiText('По умолчанию') }] : []),
          ...field.values.map(value => ({
            value,
            label: uiText(field.key === 'count' ? `${value} фото` : (valueLabels?.[value] ?? photoValues[value] ?? value)),
            description:
              descriptions?.[value] !== undefined || (prices ?? field.prices)?.[value] !== undefined
                ? [descriptions?.[value] ? uiText(descriptions[value]) : '', (prices ?? field.prices)?.[value] !== undefined ? `${(prices ?? field.prices)![value]} C` : '']
                    .filter(Boolean)
                    .join(' · ')
                : field.key === 'format'
                ? uiText(
                    (
                      {
                        auto: 'Без обрезки, как снято',
                        '1:1': 'Для аватарки и ленты',
                        '3:4': 'Как снимок с телефона',
                        '4:5': 'Для ленты',
                        '9:16': 'На весь экран телефона',
                        '16:9': 'Для обложки и экрана',
                      } as Record<string, string>
                    )[value] ?? '',
                  )
                : undefined,
            icon:
              field.key === 'format' ? (
                <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                  <rect
                    x={value === '9:16' ? 9 : 5}
                    y={value === '16:9' ? 9 : 5}
                    width={value === '9:16' ? 14 : 22}
                    height={value === '16:9' ? 14 : 22}
                    rx="3"
                  />
                </svg>
              ) : field.key === 'count' ? (
                <span className="mono">{value}</span>
              ) : undefined,
          })),
        ]}
        onChange={setValue}
      />
    );
  return (
    <label className="photo-field">
      {uiText(label ?? photoLabels[field.key] ?? 'Дополнительная настройка')}
      <input
        disabled={state.locked}
        maxLength={limit}
        value={state.inputs[field.key] ?? ''}
        onChange={event => setValue(event.target.value)}
      />
    </label>
  );
}
